<script setup lang="ts">
/**
 * The screen for people who cannot get in yet.
 *
 * Shown INSTEAD of the app, and only to callers who would otherwise hit a
 * dead end: no Entra session, or a session without the app-access group.
 * Anyone entitled never sees it, which is what makes it safe for this to be
 * the one properly graphic surface in the product — it costs the daily
 * driver nothing, and it is the only page a stranger can ever reach.
 *
 * One action. It used to offer two doors — sign in, or sign up as a beta
 * tester — and the beta signup was retired on 2026-10-27: access is granted
 * through the organisation, not requested from a public page. What remains
 * is the photograph, the name, and a single sign-in.
 *
 * Someone already signed in but not entitled cannot be helped by "sign in"
 * — they just did. For them the page says so plainly, tells them who can
 * grant access, and offers a quieter switch to another account (with the
 * account picker, so it does not silently sign the same person in again).
 */
import { computed, ref } from "vue";
import { login } from "../auth/msal";
import { brandRef } from "../brand-state";
import BrandSquare from "./BrandSquare.vue";

const props = defineProps<{
  state: "signed-out" | "not-entitled";
  signedInAs?: string;
  signedInName?: string;
  /** Why the gate is showing — see SsoFailure. Kept on the props for the
   *  callers; the page itself no longer records it anywhere. */
  reason?: string;
}>();

const productName = computed(() => brandRef.value.productName || "DOIT AI");

/** The hero is a dark photograph, so the reversed logo is the correct one.
 *  Falls back to the standard mark for a brand that ships only one. */
const logoUrl = computed(
  () => brandRef.value.logoUrlInverse || brandRef.value.logoUrl || "",
);

const signInLabel = computed(
  () => brandRef.value.loginButtonLabel || "Log ind med Microsoft",
);

/** The operator owns this string via brand.json; the fallback only applies
 *  to a deploy that has not set one. */
const lede = computed(
  () =>
    brandRef.value.tagline ||
    "Din organisations egen AI-assistent — bygget på jeres egen viden.",
);

const notEntitled = computed(() => props.state === "not-entitled");

const busy = ref(false);

async function onSignIn(): Promise<void> {
  busy.value = true;
  try {
    await login({ selectAccount: notEntitled.value });
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div class="gate">
    <!-- The hero is decorative; the page reads correctly without it, which
         is why it carries no alt text and the scrim is a sibling rather than
         something the content depends on. -->
    <div class="gate__hero" aria-hidden="true"></div>
    <div class="gate__scrim" aria-hidden="true"></div>

    <main class="gate__inner">
      <header class="gate__brand">
        <img v-if="logoUrl" :src="logoUrl" alt="" class="gate__logo" />
        <BrandSquare v-else size="2rem" />
      </header>

      <h1 class="gate__title">{{ productName }}</h1>
      <p class="gate__lede">{{ lede }}</p>

      <p v-if="notEntitled" class="gate__status" role="status">
        <template v-if="signedInAs">
          Du er logget ind som <strong>{{ signedInAs }}</strong>, men din konto
        </template>
        <template v-else>Din konto</template>
        er ikke åbnet for {{ productName }} endnu. Kontakt din leder eller IT
        for at få adgang.
      </p>

      <button
        type="button"
        class="gate__signin"
        :class="{ 'gate__signin--quiet': notEntitled }"
        :disabled="busy"
        @click="onSignIn"
      >
        <!-- Microsoft's four-square mark, as Microsoft's own sign-in
             buttons carry it: it says which account is meant before the
             label is read. -->
        <svg class="gate__ms" viewBox="0 0 21 21" aria-hidden="true">
          <rect x="1" y="1" width="9" height="9" fill="#f25022" />
          <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
          <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
          <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
        </svg>
        <span>{{ notEntitled ? "Log ind med en anden konto" : signInLabel }}</span>
      </button>
    </main>
  </div>
</template>

<style scoped>
.gate {
  position: relative;
  min-height: 100dvh;
  display: grid;
  place-items: center;
  padding: var(--space-4) var(--space-3);
  isolation: isolate;
}
/* /welcome-hero.jpg comes from the client's brand/ overlay (ADR 0080); the
   product build has none and shows the solid colour. Use a dark photograph:
   it is the only kind white text sits on and still meets contrast. */
.gate__hero {
  position: fixed;
  inset: 0;
  z-index: -2;
  background:
    url("/welcome-hero.jpg") center 60% / cover no-repeat,
    #12243a;
}
/* Scrim. The top needs depth for the headline, the middle stays clear so the
   building is visible, and the bottom darkens again. */
.gate__scrim {
  position: fixed;
  inset: 0;
  z-index: -1;
  /* A soft pool of dark behind the text column, over the vertical wash: the
     tagline otherwise sits on lit windows and loses contrast, while a heavier
     flat wash would bury the building the page is built around. */
  background:
    radial-gradient(
      ellipse 42rem 22rem at 50% 44%,
      rgba(10, 22, 38, 0.62) 0%,
      rgba(10, 22, 38, 0.32) 55%,
      transparent 100%
    ),
    linear-gradient(
      180deg,
      rgba(10, 22, 38, 0.8) 0%,
      rgba(10, 22, 38, 0.45) 45%,
      rgba(10, 22, 38, 0.82) 100%
    );
}
@media (prefers-reduced-motion: no-preference) {
  .gate__inner {
    animation: gate-rise var(--dur-3, 520ms) var(--ease-out, ease-out) both;
  }
}
@keyframes gate-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
/* One centred column: logo, name, tagline, button — sitting in the upper
   middle so the building stays the picture rather than the background to a
   form. */
.gate__inner {
  width: min(36rem, 100%);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateY(-7vh);
}
.gate__brand {
  display: flex;
  justify-content: center;
  margin-bottom: var(--space-3);
  /* Above the scrim's darkest stop, so the reversed logo reads. */
  filter: drop-shadow(0 1px 10px rgba(0, 0, 0, 0.4));
}
.gate__logo {
  height: 2.25rem;
  width: auto;
}
.gate__title {
  margin: 0;
  color: #fff;
  text-shadow: 0 1px 24px rgba(0, 0, 0, 0.45);
  font-family: var(--font-display, inherit);
  font-size: clamp(2.5rem, 7vw, 4rem);
  line-height: 1.02;
  letter-spacing: -0.02em;
}
.gate__lede {
  margin: var(--space-2) auto 0;
  max-width: 30rem;
  /* Not --color-muted: that token is tuned for a light surface and drops
     below contrast on the photograph. */
  color: rgba(255, 255, 255, 0.88);
  font-size: 1.08rem;
}
.gate__status {
  margin: var(--space-4) auto 0;
  max-width: 30rem;
  padding: 0.7rem 1rem;
  border-left: 3px solid var(--color-highlight);
  background: rgba(255, 255, 255, 0.1);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  color: #fff;
  text-align: left;
  font-size: 0.92rem;
  line-height: 1.45;
}
/* The one action. Near-white on the dark photograph: the strongest contrast
   the page has, so it reads as THE thing to do without needing a colour of
   its own, and the Microsoft mark sits naturally on it. */
.gate__signin {
  margin-top: var(--space-5, 2.25rem);
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.85rem 1.6rem;
  min-width: 15rem;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: var(--radius-pill, 999px);
  background: rgba(255, 255, 255, 0.95);
  color: #0a1626;
  font: inherit;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.005em;
  cursor: pointer;
  box-shadow: 0 10px 34px rgba(0, 0, 0, 0.32);
  transition:
    transform var(--dur-1, 140ms) var(--ease-out, ease-out),
    box-shadow var(--dur-1, 140ms) var(--ease-out, ease-out),
    background var(--dur-1, 140ms) var(--ease-out, ease-out);
}
.gate__signin:hover:not(:disabled) {
  background: #fff;
  transform: translateY(-1px);
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.38);
}
.gate__signin:focus-visible {
  outline: 3px solid var(--color-highlight, #fff);
  outline-offset: 3px;
}
.gate__signin:disabled {
  opacity: 0.6;
  cursor: wait;
}
/* For someone already signed in, switching account is the secondary path:
   glass instead of solid, so the notice above stays the focus. */
.gate__signin--quiet {
  margin-top: var(--space-3);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  box-shadow: none;
}
.gate__signin--quiet:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
}
.gate__ms {
  width: 1.1rem;
  height: 1.1rem;
  flex: none;
}
@media (max-width: 30rem) {
  .gate__inner {
    transform: none;
  }
  .gate__signin {
    min-width: 0;
    width: 100%;
  }
}
</style>

<style>
/* The app reserves a scrollbar gutter on every page (styles/app.css) so
   content does not shift when one appears. This page never scrolls, so the
   reserved strip would show as a bare band down the right edge of the
   photograph on Windows browsers with classic scrollbars. */
html:has(.gate) {
  scrollbar-gutter: auto;
}
</style>
